import { lazy, Suspense, useId, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AccountPanel } from "../auth/AccountPanel";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, DownloadIcon, LanguageIcon, MonitorIcon, MoonIcon, SunIcon, UploadIcon } from "../components/Icons";
import { usePreferences } from "../preferences/usePreferences";
import type { BloodPressureThresholds, LanguagePreference, ThemePreference, WeightUnit } from "../preferences/types";
import { createRecords, fetchAllRecords } from "../api/client";
import { invalidateMeasurements } from "../api/invalidation";
import { maximumBackupRecords, parseBackup, serializeBackup } from "../lib/dataBackup";
import { defaultBloodPressureThresholds } from "../preferences/storage";
import type { NewHealthRecord } from "../types";

const languages: LanguagePreference[] = ["system", "en", "hr"];
const BackupDialog = lazy(() => import("../therapy/BackupDialog").then((module) => ({ default: module.BackupDialog })));
const themes: ThemePreference[] = ["system", "light", "dark"];
const weightUnits: WeightUnit[] = ["kg", "lb"];

export function PreferencesPage() {
  const { t } = useTranslation();
  const preferences = usePreferences();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingImport, setPendingImport] = useState<NewHealthRecord[]>();
  const [dataMessage, setDataMessage] = useState<string>();
  const [dataError, setDataError] = useState<string>();
  const [therapyBackup, setTherapyBackup] = useState(false);
  const importData = useMutation({
    mutationFn: createRecords,
    onSuccess: async (count) => {
      await invalidateMeasurements(queryClient);
      setPendingImport(undefined);
      setDataError(undefined);
      setDataMessage(t("preferences.importComplete", { count }));
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: () => setDataError(t("preferences.importFailed")),
  });

  async function exportData() {
    setDataError(undefined);
    try {
      const records = await fetchAllRecords();
      if (records.length > maximumBackupRecords) {
        setDataError(t("preferences.backupLimitExceeded", { count: maximumBackupRecords }));
        return;
      }
      const blob = new Blob([serializeBackup(records)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `health-tracker-backup-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setDataMessage(t("preferences.exportComplete", { count: records.length }));
    } catch {
      setDataError(t("preferences.exportFailed"));
    }
  }

  async function selectImport(event: ChangeEvent<HTMLInputElement>) {
    setDataError(undefined);
    setDataMessage(undefined);
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setPendingImport(parseBackup(await file.text()));
    } catch {
      setPendingImport(undefined);
      setDataError(t("preferences.invalidBackup"));
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6"><AccountPanel /></div>
      <header className="border-b border-line pb-7">
        <p className="page-eyebrow">{t("preferences.eyebrow")}</p>
        <h1 className="page-title">{t("preferences.title")}</h1>
        <p className="page-description mt-3">{t("preferences.description")}</p>
      </header>

      <div className="surface-elevated mt-8 divide-y divide-line overflow-hidden rounded-[1.4rem]">
        <PreferenceSection title={t("preferences.languageTitle")} description={t("preferences.languageDescription")}>
          <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label={t("preferences.languageTitle")}>
            {languages.map((language) => (
              <PreferenceChoice
                key={language}
                name="language"
                value={language}
                checked={preferences.language === language}
                onChange={() => preferences.setLanguage(language)}
                label={languageLabel(language, t)}
                icon={language === "system" ? <LanguageIcon className="size-4" /> : <span className="text-xs font-black tracking-wide">{language.toUpperCase()}</span>}
              />
            ))}
          </div>
          <p className="mt-3 text-xs font-semibold text-muted">{t("preferences.currentLanguage", { language: languageLabel(preferences.effectiveLanguage, t) })}</p>
        </PreferenceSection>

        <PreferenceSection title={t("preferences.appearanceTitle")} description={t("preferences.appearanceDescription")}>
          <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label={t("preferences.appearanceTitle")}>
            {themes.map((theme) => (
              <PreferenceChoice
                key={theme}
                name="theme"
                value={theme}
                checked={preferences.theme === theme}
                onChange={() => preferences.setTheme(theme)}
                label={t(`preferences.${theme}`)}
                icon={themeIcon(theme)}
              />
            ))}
          </div>
          <p className="mt-3 text-xs font-semibold text-muted">{t("preferences.currentTheme", { theme: t(`preferences.${preferences.effectiveTheme}`) })}</p>
        </PreferenceSection>

        <PreferenceSection title={t("preferences.unitsTitle")} description={t("preferences.unitsDescription")}>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t("preferences.unitsTitle")}>
            {weightUnits.map((unit) => <PreferenceChoice key={unit} name="weight-unit" value={unit} checked={preferences.weightUnit === unit} onChange={() => preferences.setWeightUnit(unit)} label={t(`preferences.weightUnit.${unit}`)} icon={<span className="text-xs font-black">{unit}</span>} />)}
          </div>
        </PreferenceSection>

        <PreferenceSection title={t("preferences.thresholdsTitle")} description={t("preferences.thresholdsDescription")}>
          <ThresholdGroup values={preferences.bloodPressureThresholds} onChange={preferences.setBloodPressureThresholds} />
          <button type="button" onClick={() => preferences.setBloodPressureThresholds(defaultBloodPressureThresholds)} className="pressable mt-3 min-h-11 rounded-xl border border-line px-4 py-2 text-xs font-bold text-muted hover:bg-canvas hover:text-ink focus-ring">{t("preferences.restoreEscThresholds")}</button>
          <p className="mt-3 text-xs leading-5 text-muted">{t("preferences.thresholdDisclaimer")}</p>
        </PreferenceSection>

        <PreferenceSection title={t("preferences.dataTitle")} description={t("preferences.dataDescription")}>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void exportData()} className="pressable inline-flex min-h-11 items-center gap-2 rounded-xl border border-line-strong bg-surface px-4 py-2.5 text-sm font-bold hover:bg-canvas focus-ring"><DownloadIcon className="size-4" />{t("preferences.exportJson")}</button>
            <button type="button" onClick={() => fileInputRef.current?.click()} className="pressable inline-flex min-h-11 items-center gap-2 rounded-xl border border-line-strong bg-surface px-4 py-2.5 text-sm font-bold hover:bg-canvas focus-ring"><UploadIcon className="size-4" />{t("preferences.chooseBackup")}</button>
            <input ref={fileInputRef} type="file" accept="application/json,.json" onChange={(event) => void selectImport(event)} className="sr-only" tabIndex={-1} aria-hidden="true" />
          </div>
          {pendingImport ? (
            <div className="mt-4 rounded-xl border border-appointment/35 bg-appointment/10 p-4">
              <p className="text-sm font-semibold">{t("preferences.importConfirmation", { count: pendingImport.length })}</p>
              <p className="mt-1 text-xs leading-5 text-muted">{t("preferences.importDuplicateNotice")}</p>
              <div className="mt-3 flex gap-2">
                <button type="button" disabled={importData.isPending} onClick={() => importData.mutate(pendingImport)} className="pressable min-h-11 rounded-xl bg-teal-700 px-4 py-2 text-sm font-bold text-canvas hover:bg-teal-800 focus-ring disabled:opacity-50">{importData.isPending ? t("preferences.importing") : t("preferences.confirmImport")}</button>
                <button type="button" disabled={importData.isPending} onClick={() => setPendingImport(undefined)} className="pressable min-h-11 rounded-xl border border-line px-4 py-2 text-sm font-bold text-muted hover:bg-canvas focus-ring">{t("common.cancel")}</button>
              </div>
            </div>
          ) : null}
          {dataMessage ? <p role="status" className="mt-3 text-sm font-semibold text-exercise-strong">{dataMessage}</p> : null}
          {dataError ? <p role="alert" className="mt-3 text-sm font-semibold text-vitals-strong">{dataError}</p> : null}
        </PreferenceSection>
      </div>

      <div className="surface-elevated mt-5 rounded-[1.4rem]"><PreferenceSection title={t("therapy.backupTitle")} description={t("therapy.backupScope")}><button className="pressable min-h-11 rounded-xl border border-line-strong bg-surface px-4 py-2 text-sm font-semibold focus-ring" onClick={() => setTherapyBackup(true)}>{t("therapy.backupManage")}</button></PreferenceSection></div>
      {therapyBackup ? <Suspense fallback={<p role="status">{t("therapy.loading")}</p>}><BackupDialog onClose={() => setTherapyBackup(false)} /></Suspense> : null}
      <p className="mt-5 flex gap-3 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm leading-6 text-teal-900">
        <span aria-hidden="true" className="mt-1 grid size-4 shrink-0 place-items-center rounded-full bg-teal-200 text-[0.6rem] font-black text-teal-950">i</span>
        <span>{t("preferences.localNotice")}</span>
      </p>
    </div>
  );
}

function ThresholdGroup({ values, onChange }: { values: BloodPressureThresholds; onChange: (value: BloodPressureThresholds) => void }) {
  const { t } = useTranslation();
  return (
    <fieldset className="rounded-xl border border-line bg-canvas p-3">
      <legend className="px-1 text-xs font-bold text-ink">{t("preferences.homeThresholds")}</legend>
      <div className="grid grid-cols-2 gap-2">
        <ThresholdInput key={`systolic-${values.systolic}`} label={t("recordForm.systolic")} value={values.systolic} min={80} onChange={(value) => onChange({ ...values, systolic: value })} />
        <ThresholdInput key={`diastolic-${values.diastolic}`} label={t("recordForm.diastolic")} value={values.diastolic} min={40} onChange={(value) => onChange({ ...values, diastolic: value })} />
      </div>
    </fieldset>
  );
}

function ThresholdInput({ label, value, min, onChange }: { label: string; value: number; min: number; onChange: (value: number) => void }) {
  const { t } = useTranslation();
  const helperId = useId();
  const [draft, setDraft] = useState(String(value));
  const numericDraft = Number(draft);
  const invalid = draft !== "" && (!Number.isFinite(numericDraft) || numericDraft < min || numericDraft > 250);

  function commit() {
    if (draft.trim() === "" || !Number.isFinite(numericDraft)) {
      setDraft(String(value));
      return;
    }
    const next = Math.min(250, Math.max(min, Math.round(numericDraft)));
    setDraft(String(next));
    if (next !== value) onChange(next);
  }

  return (
    <label className="block">
      <span className="field-label">{label}</span>
      <span className="relative block">
        <input type="number" min={min} max="250" value={draft} aria-describedby={helperId} aria-invalid={invalid} onChange={(event) => setDraft(event.target.value)} onBlur={commit} onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }} className="field-input pr-12" />
        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-[0.6rem] font-bold text-muted">mmHg</span>
      </span>
      <span id={helperId} className={`mt-1 block text-[0.68rem] font-semibold ${invalid ? "text-vitals-strong" : "text-muted"}`}>{t("preferences.thresholdRange", { min, max: 250 })}</span>
    </label>
  );
}

function PreferenceChoice({ name, value, checked, onChange, label, icon }: { name: string; value: string; checked: boolean; onChange: () => void; label: string; icon: ReactNode }) {
  return (
    <label className="choice-control preference-choice pressable">
      <input className="sr-only" type="radio" name={name} value={value} checked={checked} onChange={onChange} />
      <span className="flex min-w-0 items-center gap-2.5">
        <span aria-hidden="true" className="preference-choice__icon">{icon}</span>
        <span className="text-balance leading-5">{label}</span>
      </span>
      <span aria-hidden="true" className={`grid size-5 shrink-0 place-items-center rounded-full border ${checked ? "border-teal-600 bg-teal-600 text-canvas" : "border-line-strong text-transparent"}`}>
        <CheckIcon className="size-3" />
      </span>
    </label>
  );
}

function PreferenceSection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <section className="grid gap-6 p-5 sm:p-7 lg:grid-cols-[minmax(12rem,0.7fr)_minmax(0,1.3fr)]">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        <p className="mt-1 max-w-md text-sm leading-6 text-muted">{description}</p>
      </div>
      <div>{children}</div>
    </section>
  );
}

function languageLabel(language: LanguagePreference, t: ReturnType<typeof useTranslation>["t"]): string {
  if (language === "en") return t("preferences.english");
  if (language === "hr") return t("preferences.croatian");
  return t("common.automatic");
}

function themeIcon(theme: ThemePreference): ReactNode {
  if (theme === "light") return <SunIcon className="size-4" />;
  if (theme === "dark") return <MoonIcon className="size-4" />;
  return <MonitorIcon className="size-4" />;
}
